import { cva } from 'class-variance-authority';

import { Button } from 'ui/button';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from 'ui/dialog';

import { Translation } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { useDialogFormStore } from './form.store';

const dialogContentStyles = cva('p-0 gap-0 rounded bg-card flex flex-col max-h-[90vh] border-none', {
  variants: {
    size: {
      sm: 'sm:max-w-[327px]',
      md: 'sm:max-w-[416px]',
      lg: 'sm:max-w-[556px] ',
      img: 'w-[310px] h-[420px]',
      qr: 'w-[280px] h-auto',
      doc: 'md:max-w-[556px]   h-[80vh] max-w-none',
      p0: 'sm:max-w-[556px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const formContainerStyles = cva('flex items-center pt-4', {
  variants: {
    size: {
      sm: 'px-4 pb-6',
      md: 'px-6 pb-6',
      lg: 'px-6 pb-8',
      img: 'p-0',
      qr: 'p-0',
      doc: 'p-0',
      p0: 'p-0',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const dialogHeaderStyles = cva('px-4 relative border-b-[2px] border-b-border-divider', {
  variants: {
    size: {
      sm: 'px-4 py-3.5',
      md: 'px-5 py-3',
      lg: 'px-6 py-4',
      img: 'p-0',
      qr: 'p-0',
      doc: 'p-0',
      p0: 'px-6 py-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export const FormDialog = () => {
  const {
    modalTitle,
    open,
    onClose,
    size,
    okText,
    cancelText,
    component,
    formId,
    image,
    onCancel,
    isSubmitting,
    setSubmitting,
    okButtonDisabled,
  } = useDialogFormStore();

  const formState = useDialogFormStore((state) => state.formState);

  const submitting = formState?.isSubmitting || isSubmitting === 'submit';

  const handleCancel = async () => {
    try {
      setSubmitting?.('cancel');
      await Promise.resolve(onCancel?.());
      onClose();
    } catch {
      // The form handles its own user-facing error state.
    } finally {
      setSubmitting?.(null);
    }
  };

  return (
    <div>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isSubmitting === null && !formState?.isSubmitting) {
            onClose();
          }
        }}
      >
        <DialogContent
          onPointerDownOutside={(event) => {
            const target = event.target as HTMLElement;

            if (target.closest('[data-slot="combobox-content"]')) {
              event.preventDefault();
            }
          }}
          className={cn(dialogContentStyles({ size }))}
        >
          {!image && (
            <DialogHeader className={dialogHeaderStyles({ size })}>
              <div className="flex justify-between items-center">
                {modalTitle && (
                  <DialogTitle className="text-base">
                    <Translation id={modalTitle} />
                  </DialogTitle>
                )}
                <div className="flex gap-3">
                  {cancelText && (
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() => {
                        void handleCancel();
                      }}
                      isLoading={isSubmitting === 'cancel'}
                      disabled={isSubmitting === 'cancel'}
                      className="bg-card text-muted-foreground"
                    >
                      {cancelText}
                    </Button>
                  )}

                  {okText && (
                    <Button
                      type="submit"
                      variant="default"
                      form={formId}
                      isLoading={submitting}
                      disabled={submitting || okButtonDisabled}
                    >
                      {okText}
                    </Button>
                  )}
                </div>
              </div>

              <DialogClose className="absolute -top-10 left-4" />
            </DialogHeader>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className={cn(formContainerStyles({ size }))}>{component}</div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
